import { useEffect, useRef, useState } from 'react';

/**
 * Hook that watches a bottom marker div using IntersectionObserver.
 * When the marker becomes visible, it enables the accept button.
 *
 * @param options.alreadyAccepted - If true, bypasses scroll detection and returns hasScrolledToBottom: true
 */
export const useScrollToAccept = (options: { alreadyAccepted?: boolean } = {}) => {
  const { alreadyAccepted = false } = options;

  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const bottomMarkerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (alreadyAccepted) {
      return;
    }

    const setupObserver = () => {
      const marker = bottomMarkerRef.current;

      if (!marker) {
        setTimeout(setupObserver, 100);
        return null;
      }

      const observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (entry.isIntersecting) {
            setHasScrolledToBottom(true);
          }
        },
        {
          threshold: 0.1,
        },
      );

      observer.observe(marker);
      return observer;
    };

    const observer = setupObserver();

    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  }, [alreadyAccepted]);

  return {
    hasScrolledToBottom: alreadyAccepted || hasScrolledToBottom,
    setHasScrolledToBottom,
    bottomMarkerRef,
  };
};
